import { useCallback } from 'react';
import {
    ONBOARDING_EXTERNAL_CHECKOUT_STEPS,
    ONBOARDING_HAS_CLICKED_ON_PUBLISH_STORAGE_KEY,
    useOnboardingStage,
} from '#shared/api/onboarding';
import { successSrc } from '#shared/assets';
import { YCP_CHECKOUT_PUBLISH } from '#shared/lib/const';
import { useExperimentFlag } from '#shared/lib/flags';
import { useConfirm } from '#shared/lib/hooks';
import { persistentStorage } from '#shared/lib/storage';
import { showError } from '#shared/lib/toast';
import { trackMerchantsGoalError, trackMerchantsGoalSuccess } from '#shared/lib/trackGoal';
import { type VariantPublishButtonProps } from '../OnboardingStepsRenderer';
import { PublishButton } from '../PublishButton';

export function ExternalPublishButton({ name, isDisabled, 'data-testid': dataTestId }: VariantPublishButtonProps) {
    const { complete, isLoading } = useOnboardingStage(ONBOARDING_EXTERNAL_CHECKOUT_STEPS.PUBLISH);
    const { renderConfirmModal, confirm } = useConfirm();
    const isTemporarilyDisabled = !useExperimentFlag('enable_ycp_publish_btn_and_hide_alert');

    const handleClick = useCallback(async () => {
        if (isTemporarilyDisabled) {
            return;
        }

        try {
            await complete();
            persistentStorage.setItem(ONBOARDING_HAS_CLICKED_ON_PUBLISH_STORAGE_KEY, '1');
            trackMerchantsGoalSuccess(YCP_CHECKOUT_PUBLISH);
            confirm({
                imageSrc: successSrc,
                title: 'Все условия выполнены, кнопка «Купить в 1 клик» в процессе публикации',
                bodyText: 'Процесс публикации может занять до 36 часов.',
                applyText: 'Понятно',
                hideCancel: true,
            });
        } catch (error) {
            showError(
                {
                    durationMs: 5000,
                    label: 'Ошибка публикации. Для продолжения работы убедитесь, что права на домен выданы на вашу почту в Вебмастере.',
                },
                error
            );
            trackMerchantsGoalError(YCP_CHECKOUT_PUBLISH, { error });
        }
    }, [complete, confirm, isTemporarilyDisabled]);

    return (
        <>
            {renderConfirmModal()}
            <PublishButton
                data-testid={dataTestId}
                name={name}
                isDisabled={isTemporarilyDisabled || isDisabled}
                isLoading={isLoading}
                onClick={handleClick}
                tooltip={
                    isTemporarilyDisabled
                        ? 'Публикация универсального чекаута временно недоступна. Сообщим, когда восстановим.'
                        : 'Чтобы опубликовать кнопку «Купить в 1 клик» на поиске Яндекса  — завершите все предыдущие шаги'
                }
            />
        </>
    );
}
